import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { NavigationBar, NavigationConfigs } from '@/components/layout';
import QRCodeGenerator from '@/components/qr/QRCodeGenerator';
import useRestaurantStore from '@/lib/stores/restaurantStore';
import useAuthStore from '@/lib/stores/authStore';
import { 
  QrCode,
  Eye
} from 'lucide-react';
import { ROUTES, type QRCodeConfig } from '@/types';

/**
 * QR Code Management Page
 * 
 * Features:
 * - QR code generation and customization
 * - Download in multiple formats
 * - Restaurant context
 */
const QRCodeManagementPage: React.FC = () => {
  const { id: restaurantId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { restaurants, loading } = useRestaurantStore();
  
  const [currentConfig, setCurrentConfig] = useState<QRCodeConfig | null>(null);

  // Find current restaurant
  const restaurant = restaurants.find(r => r.id === restaurantId);

  // Redirect if restaurant not found or user not authenticated
  useEffect(() => {
    if (!loading && (!restaurant || !user)) {
      navigate(ROUTES.RESTAURANTS);
    }
  }, [restaurant, user, loading, navigate]);

  const handleConfigChange = (config: QRCodeConfig) => {
    setCurrentConfig(config);
  };

  const handleVisitMenu = () => {
    if (currentConfig?.url) {
      window.open(currentConfig.url, '_blank');
    }
  };

  const handleBack = () => {
    navigate(ROUTES.RESTAURANTS);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <QrCode className="w-6 h-6 text-white" />
          </div>
          <p className="text-gray-600">Loading restaurant...</p>
        </div>
      </div>
    );
  }

  if (!restaurant) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <NavigationBar 
        config={NavigationConfigs.qrCodeManagement(
          restaurant?.name || 'Restaurant',
          handleBack,
          currentConfig ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleVisitMenu}
              leftIcon={<Eye className="w-4 h-4" />}
            >
              View Menu
            </Button>
          ) : undefined
        )} 
      />

      {/* Main Content */}
      <main className="max-w-12xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-6 sm:mb-8 space-y-4 sm:space-y-0">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              QR Code for {restaurant.name}
            </h2>
            <p className="text-gray-600 mt-1 sm:mt-2 text-sm sm:text-base">
              Generate a QR code that links directly to your menu
            </p>
          </div>
        </div>

        {/* QR Code Generator */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <QRCodeGenerator 
            restaurant={restaurant}
            onConfigChange={handleConfigChange}
          />
        </motion.div>
      </main>
    </div>
  );
};

export default QRCodeManagementPage;
